import { RecipeResponse } from 'lib/services/models/content-response';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { getEnumValues } from 'lib/utils/sitecore/enum';
import { Data } from '@generated/Foundation.GMI.model';
import { RecipeHeroAttribute, RecipeHeroProps } from './RecipeHero';
import { getActionToolbarButtons } from 'components/helpers/ActionToolbar/Utils';
import { Contributor } from 'lib/gql/getContributor';

/**
 * Extracts header data from a RecipeResponse object
 * @param recipe - The full recipe response object
 * @param siteSettings - Optional site settings for recipe header settings
 * @param contributor - Optional contributor data from server-side context
 * @param locale - Locale string for date formatting
 * @param dictionary - Dictionary values to avoid using hooks
 * @returns RecipeHeaderProps object with only the properties needed for the recipe header
 */
export function extractRecipeHeroData(
  recipe: RecipeResponse,
  siteSettings?:
    | (SiteSettings & {
        recipeDetailSiteSettings?: Data.Settings.RecipeDetailSiteSettings;
      })
    | null,
  contributor?: Contributor,
  locale?: string,
  dictionary?: {
    kitchenTested?: string;
    prepTime?: string;
    totalTime?: string;
    ingredients?: string;
    servings?: string;
    caloriesPerServing?: string;
    by?: string;
    published?: string;
    modifiedDate?: string;
  }
): RecipeHeroProps {
  const imageUrl =
    recipe.ExtraLargeImageUrl || recipe.StandardImageUrl || recipe.FallbackImageUrl || '';
  const imageAlt = imageUrl === recipe.FallbackImageUrl ? recipe.FallbackImageAlt || '' : '';

  // Calculate total number of ingredients
  const totalIngredients = recipe.IngredientGroups?.reduce(
    (total, group) => total + (group?.Ingredients?.length || 0),
    0
  );
  const { recipeDetailSiteSettings } = siteSettings || {};
  const rdpHeroSettings = {
    hideRdpHero: recipeDetailSiteSettings?.fields?.hideRdpHero?.value,
    hideRdpHeroImage: recipeDetailSiteSettings?.fields?.hideRdpHeroImage?.value,
    hideRdpHeroBreadcrumb: recipeDetailSiteSettings?.fields?.hideRdpHeroBreadcrumb?.value,
    hideRdpHeroRatingsAndReviews:
      recipeDetailSiteSettings?.fields?.hideRdpHeroRatingsAndReviews?.value,
    hideRdpHeroByLine: recipeDetailSiteSettings?.fields?.hideRdpHeroByLine?.value,
    hideRdpHeroByAuthorSignaturePrefix:
      recipeDetailSiteSettings?.fields?.hideRdpHeroByAuthorSignaturePrefix?.value,
    hideRdpHeroAttributeList: getEnumValues(
      recipeDetailSiteSettings?.fields?.hideRdpHeroAttributeList
    ),
    heroActionToolbarButtons: getActionToolbarButtons(
      recipeDetailSiteSettings?.fields
        ?.heroActionToolBarButtons as Data.Content.ActionToolBarButton[]
    ),
    heroActionToolbarIdentifier: recipeDetailSiteSettings?.fields?.heroEventPosition?.value,
    kitchenTestedLink: recipeDetailSiteSettings?.fields?.kitchenTestedLink?.value?.href ?? '',
    kitchenTestedLinkTarget:
      recipeDetailSiteSettings?.fields?.kitchenTestedLink?.value?.target ?? '',
  };

  return {
    imageUrl,
    imageAlt,
    title: recipe.Title,
    pageUrl: recipe.PageUrl,
    authorAttribution: recipe.AuthorAttribution ?? '',
    contributorId: recipe.ContributorId ?? '',
    authorName: contributor?.authorName || '',
    authorProfileURL: contributor?.authorProfileURL || '',
    locale,
    dictionary,
    createdDate: recipe.CreatedDate ?? '',
    modifiedDate: recipe.ModifiedDate ?? '',
    prepTime: recipe.PrepTime ?? '',
    totalTime: recipe.TotalTime ?? '',
    servingNumber: recipe.ServingNumber ?? '',
    caloriesPerServing:
      recipe.NutritionItems?.find((item) => item.TranslatedName === 'Calories')?.DisplayValue ?? '',
    totalIngredients: totalIngredients || 0,
    contentId: recipe.ContentId,
    ratingAndReviewContentId: recipe.RatingAndReviewContentId,
    isKitchenTested:
      recipe.KitchenTips && recipe.KitchenTips?.length > 0
        ? recipe.KitchenTips.some((tip) => tip.Category.toLowerCase().includes('kitchen tested'))
        : false,

    urlBreadcrumbs: recipe.UrlBreadcrumbs ?? [],
    rdpHeroSettings: {
      ...rdpHeroSettings,
      // Fix: Ensure hideRdpHeroAttributeList is of type RecipeHeroAttribute | undefined
      hideRdpHeroAttributeList:
        (rdpHeroSettings.hideRdpHeroAttributeList as unknown as RecipeHeroAttribute) ?? undefined,
    },
  };
}
